/** @jsxImportSource @emotion/react */
import React from 'react'
import { Trans } from 'react-i18next'
import { Row, Col } from 'react-bootstrap'
import { jsx, css } from '@emotion/react'
import { default_theme as theme } from '../emotion/theme'
import { compact, includes, map, size } from 'lodash'
import { SHORT_DAY_NAMES, SHORT_DAY_INDEXES_IN_DISPLAY_ORDER } from '../actions/date_helpers'
import { StandaloneCheckboxField } from './form/CheckboxField'
import { SemanticDropdownStandalone } from './form/Dropdown'
import { InlineInputField } from './form/InlineInputField'
import { DayPickerField } from './form/DayPicker'
import { FormikTimePicker } from './form/DatetimePicker'
import { FormikTimezonePicker } from './form/TimezonePicker'
import { FormikInputField } from './form/InputField'
import { Separator } from './layout/Separator'
import * as Yup from 'yup'

const phoneNumberOfficeHoursValidationSchema = Yup.object().shape({})

export const OFFICE_HOURS_VIEW_MODE_OPTIONS = [
  { value: 'in_office', label: <Trans>During work hours</Trans> },
  { value: 'out_of_office', label: <Trans>Outside work hours</Trans> },
]

export const VoiceCallPlanOfficeHoursRow = ({
  enabled,
  initialValues,
  onToggle,
  onSave,
  officeHoursViewMode,
  setOfficeHoursViewMode,
  isSaving,
  isToggleSaving,
}) => {
  const renderReadonlyApplicableDayNames = (applicable_days) => {
    const num_days = size(applicable_days)
    const applicable_days_in_order = compact(
      map(SHORT_DAY_INDEXES_IN_DISPLAY_ORDER, (day_index) =>
        includes(applicable_days, day_index) ? day_index : null
      )
    )

    return (
      <>
        {num_days === 7 && <Trans>Any day</Trans>}
        {num_days === 0 && <Trans>Never</Trans>}

        {size(applicable_days_in_order) < 7 &&
          map(applicable_days_in_order, (applicable_day, index) => (
            <>
              {index > 0 && (
                <>
                  ,<Separator variant="w3" />
                </>
              )}
              <Trans>{SHORT_DAY_NAMES[applicable_day]}</Trans>
            </>
          ))}
      </>
    )
  }

  const renderOfficeHoursFormFields = (formik_props) => {
    return (
      <div css={row_editing_fields_container_style}>
        <Row>
          <Col css={multivalue_col_style}>
            <Trans>Route call depending on work hours</Trans>
          </Col>
        </Row>

        <Separator variant="h40" />

        <Row css={row_with_wrap_style}>
          <Col css={office_hours_multi_value_col_style}>
            <div css={office_hours_label_col_style}>
              <Trans>Working days</Trans>
            </div>
            <div css={multivalue_col_style}>
              <DayPickerField
                field_name="applicable_days"
                formik_props={formik_props}
                manage_value_as_comma_separated_string={true}
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col css={office_hours_multi_value_col_style}>
            <div css={office_hours_label_col_style}>
              <Trans>Work hours</Trans>
            </div>
            <div>
              <FormikTimePicker name="start_time" formik_props={formik_props} />
            </div>
            <Separator variant="w10" />
            <div>
              <Trans>to</Trans>
            </div>
            <Separator variant="w10" />
            <div>
              <FormikTimePicker name="end_time" formik_props={formik_props} />
            </div>
            <Separator variant="w10" />
            <div>
              <FormikTimezonePicker name="timezone" formik_props={formik_props} />
            </div>
          </Col>
        </Row>
      </div>
    )
  }

  const { applicable_days, start_time, end_time } = initialValues || {}

  return (
    <div
      css={[row_style, white_row_style, enabled ? null : disabled_row_style]}
    >
      <div css={toggle_column_style}>
        <div css={toggle_checkbox_cell_style}>
          <StandaloneCheckboxField
            onChange={onToggle}
            label=""
            is_saving={isToggleSaving}
            variant="large"
            checked={enabled}
          />
        </div>
      </div>
      <div css={value_column_style}>
        <div css={multivalue_col_style}>
          <InlineInputField
            startInEditingMode={false}
            formButtonStyle="buttons"
            canEdit={enabled}
            readOnlyValue={
              <div css={readonly_row_style}>
                <Trans>Route call depending on work hours</Trans>
                <Separator variant="w10" />

                <div css={readonly_row_value_style}>
                  {renderReadonlyApplicableDayNames(applicable_days)}
                  <Separator variant="w5" />-<Separator variant="w5" />
                  {(!start_time || !end_time) && <Trans>Any time</Trans>}
                  {start_time && end_time && (
                    <>
                      {start_time}
                      <Separator variant="w5" />-<Separator variant="w5" />
                      {end_time}
                    </>
                  )}
                </div>
              </div>
            }
            formInitialValues={initialValues}
            onSave={({ values, formik_funcs, onSaved }) => {
              const normalisedValues = Object.assign({}, values, {
                applicable_days: values.applicable_days
                  ? values.applicable_days.split(',')
                  : [],
              })
              onSave({ values: normalisedValues, formik_funcs, onSaved })
            }}
            is_saving={isSaving}
            validationSchema={phoneNumberOfficeHoursValidationSchema}
            renderFormFields={renderOfficeHoursFormFields}
          />
        </div>
      </div>
    </div>
  )
}

const row_style = css`
  display: flex;
  flex-wrap: wrap;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 15px;
  width: 100%;
  margin-left: 0px;
  margin-right: 30px;
  align-items: center;
  min-height: 68px;
`

const white_row_style = css`
  background-color: #fff;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  align-items: center;
  line-height: 18px;
`

const disabled_row_style = css`
  color: ${theme.colors.dark_grey};
`

const toggle_column_style = css`
  display: flex;
  margin-right: 15px;
  min-width: 40px;
  max-width: 40px;
  align-self: flex-start;
  margin-top: 8px;
  justify-content: center;
  width: 100%;
`

const value_column_style = css`
  padding-bottom: 15px;
  flex-grow: 1;
`

const multivalue_col_style = css`
  display: flex;
  align-items: center;
  min-width: 150px;
`

const readonly_row_style = css`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
`

const readonly_row_value_style = css`
  font-weight: bold;
  display: flex;
`

const toggle_checkbox_cell_style = css`
  display: flex;
  flex-grow: 1;
`

const office_hours_label_col_style = css`
  min-width: 150px;
  width: 150px;
  margin-right: 30px;
`

const office_hours_multi_value_col_style = css`
  display: flex;
  margin-bottom: 20px;
  align-items: center;
  flex-wrap: wrap;
`

const row_editing_fields_container_style = css`
  margin-top: 10px;
`

const row_with_wrap_style = css`
  width: 100%;
`

const office_hours_selector_container_style = css`
  width: 200px;
`


